/**
 * Certificate Add-on Feature
 * Combined logic for Configuration, PDP Add-to-Cart, and Cart Synchronization.
 */

// 1. Configuration
const certAddonConfig = window.certAddonConfig || {
  enabled: true,

  // REQUIRED: Replace with the actual Variant ID of the certificate product
  certificateVariantId: 57269496414553,

  // PDP checkbox detection
  checkboxBlockId: "line_item_property_TzhBxL",
  checkboxInputSelector: 'input[type="checkbox"]',

  // Link properties (internal)
  linkIdProp: "_cert_link_id",
  parentMarkerProp: "_has_cert",
  childMarkerProp: "_is_cert",
  childForVariantProp: "_cert_for_variant",

  // Logic settings
  syncQuantities: true,
  cleanupOrphans: true,
  debug: false,
};
window.certAddonConfig = certAddonConfig;

const getUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getBundledSections = () => {
  let sectionsToBundle = new Set();
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
    bubbles: true,
    detail: { sections: sectionsToBundle }
  }));
  if (sectionsToBundle.size === 0) {
    const commonSections = ["cart-drawer", "cart-count", "header"];
    commonSections.forEach((selector) => {
      const element = document.querySelector(selector);
      if (!element) return;
      const section = element.closest(".shopify-section");
      if (section?.id) sectionsToBundle.add(section.id.replace("shopify-section-", ""));
    });
  }
  return [...sectionsToBundle];
};

const showError = (form, message) => {
  form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: message } }));
  document.dispatchEvent(new CustomEvent("cart:error", { detail: { error: message } }));
  document.dispatchEvent(new CustomEvent("toast:show", { detail: { message, tone: "error" } }));
  document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
};

const ensureSections = (cart, sectionsHtml) => {
  if (!cart.sections) cart.sections = {};
  cart.sections = sectionsHtml;
  return cart;
};

/**
 * Checks whether a line-item property key is the visible certificate checkbox label
 * (e.g. "Certificat d'Authenticité (+5€)" or "Certificate of Authenticity (+$5)").
 */
const isVisibleCertLabelProp = (key) => {
  if (!key || typeof key !== "string") return false;
  return /certificat\s+d['’ʼ]?authenticit|certificate\s+of\s+authenticity/i.test(key.trim());
};

/**
 * Checks whether a line-item property key is any certificate-related marker on a parent product.
 */
const isParentCertProp = (key) => {
  if (!key || typeof key !== "string") return false;
  const trimmed = key.trim();
  return (
    trimmed === certAddonConfig.parentMarkerProp ||
    trimmed === certAddonConfig.linkIdProp ||
    isVisibleCertLabelProp(trimmed)
  );
};

/**
 * Builds a cleaned properties payload for `/cart/change.js` that clears
 * certificate-related properties from a parent item when no paid certificate is linked.
 */
const buildCleanedParentProperties = (props) => {
  const result = {};
  if (!props || typeof props !== "object") return result;

  for (const [k, v] of Object.entries(props)) {
    if (isParentCertProp(k)) {
      // Blank out all certificate markers ("Certificat d'Authenticité (+5€)", "_has_cert", "_cert_link_id")
      // when the paid certificate item is NOT in the cart
      result[k] = "";
    } else {
      result[k] = v;
    }
  }
  return result;
};

/**
 * Returns true if a parent item has any non-empty certificate property when NO paid certificate is linked.
 */
const parentNeedsPropertyCleanup = (props, hasLinkedPaidCert) => {
  if (hasLinkedPaidCert) return false;
  if (!props || typeof props !== "object") return false;
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined || String(v).trim() === "") continue;
    if (isParentCertProp(k)) {
      return true;
    }
  }
  return false;
};

const patchRecommendationsFetch = () => {
  if (window.__certRecommendationsFetchPatched) return;
  window.__certRecommendationsFetchPatched = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
      if (url && url.includes("/recommendations/products")) {
        const text = await response.clone().text();
        if (!text.includes("product-recommendations")) {
          return new Response("<product-recommendations></product-recommendations>", {
            status: response.status,
            statusText: response.statusText,
            headers: new Headers(response.headers)
          });
        }
      }
    } catch (e) {
      if (certAddonConfig.debug) console.warn("[CertAddon] recommendations fetch patch error", e);
    }
    return response;
  };
};

// 2. PDP Add-to-Cart Logic
(() => {
  if (!certAddonConfig.enabled) return;
  patchRecommendationsFetch();

  const findCheckboxForForm = (form) => {
    const scope =
      form.closest(".product-info, .product, .shopify-section, quick-buy-drawer, modal-dialog") ||
      document;
    const scopedBlock = scope.querySelector(`[data-block-id="${certAddonConfig.checkboxBlockId}"]`);
    if (scopedBlock) {
      const cb = scopedBlock.querySelector(certAddonConfig.checkboxInputSelector);
      if (cb) return cb;
    }
    const fallbackBlock = document.querySelector(`[data-block-id="${certAddonConfig.checkboxBlockId}"]`);
    return fallbackBlock?.querySelector(certAddonConfig.checkboxInputSelector) || null;
  };

  const handleAddToCart = async (event) => {
    const form = event.target;
    
    // Only intercept forms posting to cart/add
    if (!form.action || !form.action.includes("/cart/add")) return;

    // Check for the certificate checkbox scoped to this form
    const checkbox = findCheckboxForForm(form);

    // If checkbox doesn't exist or isn't checked, let the theme handle it normally
    if (!checkbox || !checkbox.checked) return;

    // Stop the theme's default handler
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.submitter?.getAttribute("aria-busy") === "true") return;
    if (!form.checkValidity()) return form.reportValidity();

    const showLoadingBar = event.submitter?.querySelector("button-content") === null;
    let loadingStarted = false;
    const submitButtons = Array.from(form.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });

    try {
      if (certAddonConfig.debug) console.log("[CertAddon] Intercepted Add to Cart");

      const formData = new FormData(form);
      const parentId = formData.get("id");
      const quantity = parseInt(formData.get("quantity") || 1, 10);
      const linkId = getUUID();

      if (!parentId) {
        throw new Error("Variant ID is missing");
      }

      // 1) Prepare sections
      const sections = getBundledSections();
      formData.set("sections", sections.join(","));

      // 2) Add parent (keeps "Certificat d'Authenticité (+5€): Yes", "_has_cert: 1", and "_cert_link_id")
      formData.set(`properties[${certAddonConfig.parentMarkerProp}]`, "1");
      formData.set(`properties[${certAddonConfig.linkIdProp}]`, linkId);

      if (certAddonConfig.debug) console.log("[CertAddon] Adding parent");

      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
        loadingStarted = true;
      }

      const parentResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!parentResponse.ok) {
        const errorData = await parentResponse.json();
        const errorMessage = errorData["message"] || errorData["description"] || "Failed to add parent product";
        showError(form, errorMessage);
        return;
      }
      const parentResponseJson = await parentResponse.json();

      // 3) Add certificate
      const certPayload = {
        id: certAddonConfig.certificateVariantId,
        quantity: quantity,
        properties: {
          [certAddonConfig.childMarkerProp]: "1",
          [certAddonConfig.linkIdProp]: linkId,
          [certAddonConfig.childForVariantProp]: parentId
        },
        sections: sections.join(",")
      };

      if (certAddonConfig.debug) console.log("[CertAddon] Adding certificate", certPayload);

      const certResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(certPayload)
      });

      let certResponseJson = null;
      if (!certResponse.ok) {
        console.warn("[CertAddon] Failed to add certificate. Rolling back parent cert properties.", await certResponse.text());
        // Roll back "Certificat d'Authenticité (+5€)", "_has_cert" and "_cert_link_id" on the parent item
        const parentItem = parentResponseJson.hasOwnProperty("items") ? parentResponseJson["items"][0] : parentResponseJson;
        if (parentItem?.key) {
          try {
            const rollbackRes = await fetch(`${Shopify.routes.root}cart/change.js`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: parentItem.key,
                quantity: parentItem.quantity || quantity,
                properties: buildCleanedParentProperties(parentItem.properties || {}),
                sections: sections.join(",")
              })
            });
            if (rollbackRes.ok) {
              certResponseJson = await rollbackRes.json();
            }
          } catch (rollbackErr) {
            if (certAddonConfig.debug) console.warn("[CertAddon] Rollback failed", rollbackErr);
          }
        }
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: { message: "Le certificat n’a pas pu être ajouté. Veuillez réessayer.", tone: "error" }
        }));
      } else {
        certResponseJson = await certResponse.json();
      }

      // 4) Refresh cart UI with sections
      if (showLoadingBar && loadingStarted) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
        loadingStarted = false;
      }

      if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }

      const sectionsHtml = certResponseJson?.sections || parentResponseJson?.sections;
      if (!sectionsHtml) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        return;
      }
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        return;
      }
      const cart = ensureSections(await cartResponse.json(), sectionsHtml);

      const items = parentResponseJson.hasOwnProperty("items") ? parentResponseJson["items"] : [parentResponseJson];
      form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items,
          cart: cart
        }
      }));

      // Dispatch event compatible with Stretch theme
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cart
        }
      }));

      // Also trigger toast for success (parent added)
      if (window.themeVariables?.settings?.cartType === "message") {
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: {
            message: window.themeVariables?.strings?.addedToCart?.replace("{{product_title}}", items?.[0]?.title) || "Added to cart"
          }
        }));
      } else if (window.themeVariables?.settings?.cartType === "drawer" && typeof announceStatus === "function") {
        let content = [];
        items.forEach((item) => {
          content.push(window.themeVariables?.strings?.addedToCart?.replace("{{product_title}}", item?.title || "") || "");
        });
        announceStatus(content.join(", "));
      }

    } catch (error) {
      console.error("[CertAddon] Error:", error);
      document.dispatchEvent(new CustomEvent("toast:show", {
        detail: { message: error.message || "Error adding to cart", tone: "error" }
      }));
    } finally {
      submitButtons?.forEach((submitButton) => submitButton.removeAttribute("aria-busy"));
      if (showLoadingBar && loadingStarted) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }
  };

  // Use capture phase to intercept before theme's ProductForm
  document.addEventListener("submit", handleAddToCart, true);
})();

// 3. Cart Synchronization Logic
(() => {
  if (!certAddonConfig.enabled) return;

  window.__certSyncLock = false;
  let cartSyncTimer = null;
  let cartSyncInFlight = false;

  const syncCertificateLinks = async (cart) => {
    if (window.__certSyncLock) return;
    window.__certSyncLock = true;

    try {
      if (certAddonConfig.debug) console.log("[CertAddon] Syncing cart...", cart);

      const parentsByLink = new Map();
      const certsByLink = new Map();
      const updates = {};
      const propertyChanges = [];
      let hasChanges = false;

      // Step 1: Map items
      cart.items.forEach((item) => {
        const props = item.properties || {};
        const linkId = (props[certAddonConfig.linkIdProp] || "").trim();
        const isCertVariant = String(item.variant_id) === String(certAddonConfig.certificateVariantId);

        if (isCertVariant) {
          if (linkId && props[certAddonConfig.childMarkerProp] === "1") {
            if (!certsByLink.has(linkId)) {
              certsByLink.set(linkId, []);
            }
            certsByLink.get(linkId).push(item);
          }
        } else if (linkId && props[certAddonConfig.parentMarkerProp] === "1") {
          parentsByLink.set(linkId, item);
        }
      });

      // Step 2: Sync Quantities (Parent -> Child)
      if (certAddonConfig.syncQuantities) {
        parentsByLink.forEach((parent, linkId) => {
          const certs = certsByLink.get(linkId);
          if (certs && certs.length > 0) {
            // Ensure 1:1 match. If multiple certs for same link (shouldn't happen), keep first, kill others.
            const primaryCert = certs[0];
            
            if (primaryCert.quantity !== parent.quantity) {
              updates[primaryCert.key] = parent.quantity;
              hasChanges = true;
            }

            // Remove duplicates
            for (let i = 1; i < certs.length; i++) {
              updates[certs[i].key] = 0;
              hasChanges = true;
            }
          }
        });
      }

      // Step 3: Cleanup Orphans (Child with no Parent, AND Parent with no Paid Child)
      if (certAddonConfig.cleanupOrphans) {
        // 3a) Child certificate without a parent item -> remove certificate item
        certsByLink.forEach((certs, linkId) => {
          if (!parentsByLink.has(linkId)) {
            certs.forEach(cert => {
              updates[cert.key] = 0;
              hasChanges = true;
            });
          }
        });

        // 3b) Non-certificate parent item whose certificate was removed from cart (or has visible cert label property)
        // -> strip certificate properties from the parent item so it never displays unpaid certificate details!
        cart.items.forEach((item) => {
          const isCertVariant = String(item.variant_id) === String(certAddonConfig.certificateVariantId);
          if (isCertVariant) return;

          const props = item.properties || {};
          const linkId = (props[certAddonConfig.linkIdProp] || "").trim();
          const hasLinkedPaidCert = Boolean(
            linkId &&
            certsByLink.has(linkId) &&
            certsByLink.get(linkId).some((c) => updates[c.key] !== 0 && c.quantity > 0)
          );

          if (parentNeedsPropertyCleanup(props, hasLinkedPaidCert)) {
            propertyChanges.push({
              id: item.key,
              quantity: item.quantity,
              properties: buildCleanedParentProperties(props)
            });
            hasChanges = true;
          }
        });
      }

      // Step 4: Apply Updates
      if (hasChanges) {
        if (certAddonConfig.debug) console.log("[CertAddon] Applying updates:", { updates, propertyChanges });

        const sections = getBundledSections();
        let newCart = cart;

        for (const [lineKey, quantity] of Object.entries(updates)) {
          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: lineKey,
              quantity: quantity,
              sections: sections.join(",")
            })
          });
          if (response.ok) {
            newCart = await response.json();
          }
        }

        for (const change of propertyChanges) {
          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: change.id,
              quantity: change.quantity,
              properties: change.properties,
              sections: sections.join(",")
            })
          });
          if (response.ok) {
            newCart = await response.json();
          }
        }

        if (window.themeVariables?.settings?.pageType === "cart") {
          window.location.reload();
          return;
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "cert-addon:sync",
            cart: newCart
          }
        }));
      }

    } catch (e) {
      console.error("[CertAddon] Sync error:", e);
    } finally {
      window.__certSyncLock = false;
    }
  };

  const runCartSync = async () => {
    if (window.__certSyncLock || cartSyncInFlight) return;
    cartSyncInFlight = true;
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) {
        if (certAddonConfig.debug) console.warn("[CertAddon] cart.js fetch failed", response.status);
        return;
      }
      const cart = await response.json();
      syncCertificateLinks(cart);
    } catch (e) {
      if (certAddonConfig.debug) console.warn("[CertAddon] cart.js fetch error", e);
    } finally {
      cartSyncInFlight = false;
    }
  };

  // Listen for cart changes from the theme
  document.documentElement.addEventListener("cart:change", (event) => {
    if (event.detail?.baseEvent === "cert-addon:sync") return;
    if (event.detail?.cart) {
      syncCertificateLinks(event.detail.cart);
      return;
    }
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(runCartSync, 400);
  });

  const scheduleCartSync = () => {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(runCartSync, 200);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleCartSync, { once: true });
  } else {
    scheduleCartSync();
  }
  window.addEventListener("pageshow", scheduleCartSync);

})();
